import { Component, OnInit, OnDestroy, ElementRef, Renderer2, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { MatIconModule } from '@angular/material/icon';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Button } from 'ustore-ui';
import {
  FormBuilder,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { Observable, Subject, combineLatest } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';

import {
  RegisterService,
  RequiredField,
  TokenUpdateRequest,
} from '../../core/services/register.service';
import { ToastService } from '../../shared/services/toast.service';
import { FooterComponent } from '../../shared/components/footer/footer.component';
import { AuthLayoutComponent } from '../../shared/layouts/auth-layout.component';
import { CustomInputComponent } from '../../shared/components/custom-input/custom-input.component';
import { VerifyDomainComponent } from '../../shared/components/verify-domain/verify-domain.component';
import { ValidationService } from '../../core/services/validation.service';
import { DomainAuthorizationModalComponent } from '../checkout/product-config-card/domain-authorization-modal/domain-authorization-modal.component';
import { DomainAuthorizationConfirmationModalComponent } from '../checkout/product-config-card/domain-authorization-confirmation-modal/domain-authorization-confirmation-modal.component';
import { GtmService } from '../../core/services/gtm.service';
import { CustomValidators } from '../../shared/validators/custom-validators';
import { isValidDomain, isValidDomainInput } from '../../shared/validators/domain.validator';
import {
  MicrosoftTenantIdValidationParams,
  RegistroBrDomainAvailabilityResponse,
} from '../../core/models/validation.model';
import { ProductService } from '../../core/services/product.service';
import { I18nPipe } from '../../shared/pipes/i18n.pipe';
import { I18nService } from '../../core/services/i18n.service';
import { IntegrationsService } from '../../core/services/integrations.service';
import { ExternalTermsService } from '../../core/services/external-terms.service';
import { DeviceInfoService } from '../../core/services/device-info.service';
import { ChannelEventsService } from '../../core/services/channel-events.service';
import { DeviceInfo } from '../../core/models/device-info.model';
import {
  EMAIL_EMPRESARIAL_UUIDS,
  FREE_DOMAIN_INCLUDED_UUIDS,
  NO_EXISTING_DOMAIN_VARIATION_UUIDS,
  REGISTRY_DOMAIN_UUIDS,
} from '../../shared/constants/variation-uuids.constant';
import { Icon } from '../../core/models/icon.model';
import { ProductV2Category } from '../../core/models/product-v2.model';
import { IconComponent } from '../../shared/components/icon/icon.component';
import { CategoryIconMapperService } from '../../shared/services/category-icon-mapper.service';
import { DomainFeedback } from '../../core/models/domain-feedback.model';
import { BreadcrumbComponent } from '../../shared/components/breadcrumb/breadcrumb.component';
import { BreadcrumbItem } from '../../shared/components/breadcrumb/breadcrumb-item.interface';
import { UserFacade } from '../../core/store/user/user.facade';
import { AuthService } from '../../core/services/auth.service';
import { OrderSuccessGuard } from '../../core/guards/order-success.guard';
import { shouldAccessPendingOrders } from '../../core/utils/pending-orders-eligibility.util';

@Component({
  selector: 'umkt-register-token',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    MatIconModule,
    Button,
    FooterComponent,
    AuthLayoutComponent,
    CustomInputComponent,
    VerifyDomainComponent,
    I18nPipe,
    IconComponent,
    DomainAuthorizationModalComponent,
    DomainAuthorizationConfirmationModalComponent,
    BreadcrumbComponent,
  ],
  templateUrl: './register-token.component.html',
  styleUrls: [
    '../../../assets/style/login-register.component.scss',
    './register-token.component.scss',
  ],
})
export class RegisterTokenComponent implements OnInit, OnDestroy {
  private savedRequiredFields: RequiredField[] = [];
  private destroy$ = new Subject<void>();

  loading = false;
  domainValidationResult: boolean | null = null;
  ownershipManualVerificationError = false;
  selfConfirmedOwnership = false;
  googleDomainNotInUseAcknowledged = false;
  confirmedDomainOwnership = false;
  showDomainField = false;
  isMicrosoft = false;
  isGoogle = false;
  isComBr = false;
  isCom = false;
  isNet = false;
  microsoftTermsAccepted = false;
  showDomainRadioButtons = false;
  showAuthorizationModal = false;
  showAuthorizationConfirmationModal = false;
  domainNeedingAuthorization: string = '';
  private acceptanceTermId: string = '';
  private pendingDomainForRevalidation: string = '';
  microsoftTermsUrl = '';
  deviceInfo: DeviceInfo | null = null;
  tokenForm!: FormGroup;
  isLoggedInFlow = false;
  breadcrumbItems: BreadcrumbItem[] = [];

  private fb = inject(FormBuilder);
  private router = inject(Router);
  private route = inject(ActivatedRoute);
  private registerService = inject(RegisterService);
  private toastService = inject(ToastService);
  private gtm = inject(GtmService);
  private http = inject(HttpClient);
  private el = inject(ElementRef);
  private renderer = inject(Renderer2);
  private cdr = inject(ChangeDetectorRef);
  private validationService = inject(ValidationService);
  private productService = inject(ProductService);
  private i18nService = inject(I18nService);
  private integrationsService = inject(IntegrationsService);
  private externalTermsService = inject(ExternalTermsService);
  private deviceInfoService = inject(DeviceInfoService);
  private categoryMapper = inject(CategoryIconMapperService);
  private userFacade = inject(UserFacade);
  private authService = inject(AuthService);
  private orderSuccessGuard = inject(OrderSuccessGuard);
  private channelEventsService = inject(ChannelEventsService);

  productName = '';
  planName = '';
  productIcon: Icon = { name: 'produto', type: 'mondrian-icons' };

  get hasProductSummary(): boolean {
    return !!(this.productName || this.planName);
  }

  private get productUuid(): string {
    return this.tokenForm?.get('productUuid')?.value || '';
  }

  private get isFreeDomainIncludedProduct(): boolean {
    const productUuid = this.productUuid;
    if (!productUuid) return false;
    return FREE_DOMAIN_INCLUDED_UUIDS.includes(productUuid);
  }

  private get isRegistryDomainProduct(): boolean {
    const productUuid = this.productUuid;
    if (!productUuid) return false;
    return REGISTRY_DOMAIN_UUIDS.includes(productUuid);
  }

  private get isEmailEmpresarialProduct(): boolean {
    const productUuid = this.productUuid;
    if (!productUuid) return false;
    return EMAIL_EMPRESARIAL_UUIDS.includes(productUuid);
  }

  private get domainTypeRule(): 'new-only' | 'existing-only' | 'both' {
    // Google e Email Empresarial: somente domínio existente.
    if (
      this.isGoogle ||
      this.isEmailEmpresarialProduct ||
      this.savedRequiredFields.some(
        field =>
          field.name === 'google_ws_domain' || field.name === 'email_domain'
      )
    ) {
      return 'existing-only';
    }

    const productUuid = this.productUuid;
    if (!productUuid) return 'both';

    // Registro de domínio: sempre "new".
    if (
      this.isRegistryDomainProduct ||
      NO_EXISTING_DOMAIN_VARIATION_UUIDS.includes(productUuid)
    ) {
      return 'new-only';
    }

    // Presença Digital com domínio incluso: permite novo e existente.
    if (this.isFreeDomainIncludedProduct) {
      return 'both';
    }

    return 'both';
  }

  public get isExistingDomainOptionAllowed(): boolean {
    return (
      this.domainTypeRule === 'both' || this.domainTypeRule === 'existing-only'
    );
  }

  public get isNewDomainOptionAllowed(): boolean {
    return this.domainTypeRule === 'both' || this.domainTypeRule === 'new-only';
  }

  public get isNoDomainOptionAllowed(): boolean {
    return this.isEmailEmpresarialProduct;
  }

  public get isNoDomainSelected(): boolean {
    return this.tokenForm?.get('domainType')?.value === 'none';
  }

  private get needsDomainVerification(): boolean {
    return this.showDomainField && !this.isNoDomainSelected;
  }

  private enforceDomainTypeRules(): void {
    const rule = this.domainTypeRule;
    const current =
      (this.tokenForm.get('domainType')?.value as string) || 'new';
    let allowed = current;

    if (this.isEmailEmpresarialProduct) {
      allowed = current === 'none' ? 'none' : 'existing';
    } else if (rule === 'new-only') {
      allowed = 'new';
    } else if (rule === 'existing-only') {
      allowed = 'existing';
    } else if (current === 'none') {
      allowed = 'new';
    }

    if (current !== allowed) {
      this.tokenForm.patchValue({ domainType: allowed }, { emitEvent: false });
      const domainControl = this.tokenForm.get('domain');
      if (domainControl?.value) {
        domainControl.setValue('', { emitEvent: false });
      }
      this.domainValidationResult = null;
    }

    this.updateDomainValidation();
  }

  get pageTitleKey(): string {
    return this.isLoggedInFlow
      ? 'pages.registerToken.loggedIn.title'
      : 'pages.registerToken.title';
  }

  get pageDescriptionKey(): string {
    return this.isLoggedInFlow
      ? 'pages.registerToken.loggedIn.description'
      : 'pages.registerToken.description';
  }

  get submitButtonKey(): string {
    return this.isLoggedInFlow
      ? 'pages.registerToken.loggedIn.submitButton'
      : 'pages.registerToken.submitButton';
  }

  ngOnInit(): void {
    this.isLoggedInFlow = this.route.snapshot.data['loggedInFlow'] === true;
    this.initializeForm();

    if (this.isLoggedInFlow) {
      this.validateLoggedInAccess();
      this.updateBreadcrumb();
      this.prefillEmailFromUser();
      this.i18nService
        .getCurrentLanguage()
        .pipe(takeUntil(this.destroy$))
        .subscribe(() => this.updateBreadcrumb());
    }

    this.captureDeviceInfo();
    this.checkTokenInUrl();
    this.setupFormListeners();
  }

  private validateLoggedInAccess(): void {
    combineLatest([this.userFacade.getLoginData().pipe(take(1))])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([loginData]) => {
        if (!shouldAccessPendingOrders(loginData, this.authService)) {
          this.router.navigate(['/my-account/orders']);
        }
      });
  }

  private prefillEmailFromUser(): void {
    this.userFacade
      .getUserDetails()
      .pipe(take(1), takeUntil(this.destroy$))
      .subscribe(user => {
        const emailControl = this.tokenForm.get('email');
        if (user?.email && !emailControl?.value) {
          emailControl?.setValue(user.email);
        }
      });
  }

  private updateBreadcrumb(): void {
    this.breadcrumbItems = [
      {
        label: this.i18nService.instant('shared.labels.myAccount'),
        url: '/my-account',
      },
      {
        label: this.i18nService.instant('pages.pendingOrders.title'),
        url: '/my-account/pending-orders',
      },
      {
        label: this.i18nService.instant(
          'pages.registerToken.loggedIn.breadcrumbComplete'
        ),
        url: '/my-account/pending-orders/complete',
      },
    ];
  }

  goBackToPendingOrders(): void {
    this.router.navigate(['/my-account/pending-orders']);
  }

  private captureDeviceInfo(): void {
    this.deviceInfoService
      .getDeviceInfo()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: deviceInfo => {
          this.deviceInfo = deviceInfo;
        },
        error: error => {
          console.error('Erro ao capturar informações do dispositivo:', error);
        },
      });
  }

  private checkTokenInUrl(): void {
    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      if (params['token']) {
        this.fetchCompanyData(params['token']);
      } else if (this.isLoggedInFlow) {
        this.router.navigate(['/my-account/pending-orders']);
      } else {
        this.router.navigate(['/register']);
      }
    });
  }

  private fetchCompanyData(token: string): void {
    this.loading = true;
    this.integrationsService
      .fetchCompanyData(token)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (data: unknown) => {
          const d = data as Partial<{
            companyName: string;
            companyDocument: string;
            productUuid: string;
            userUuid: string;
            requiredFields: RequiredField[];
          }> | null;

          const companyName = d?.companyName || '';
          const companyDocument = d?.companyDocument || '';
          const productUuid = d?.productUuid || '';
          const userUuid = d?.userUuid || '';

          this.tokenForm.patchValue(
            {
              cnpj: companyDocument,
              razaoSocial: companyName,
              productUuid: productUuid,
              userUuid: userUuid,
            },
            { emitEvent: false }
          );

          this.tokenForm.updateValueAndValidity();
          this.cdr.detectChanges();

          const initialRequiredFields = d?.requiredFields || [];
          const hasDomainInInitial = this.hasRequiredFieldsInInitial(
            initialRequiredFields
          );

          if (hasDomainInInitial) {
            this.showDomainField = true;
            this.savedRequiredFields = initialRequiredFields;
            this.checkDomainType();
            this.enforceDomainTypeRules();
            this.maybeLoadMicrosoftTerms();
            this.updateDomainValidation();
            this.loading = false;
            this.loadVariationSummary(productUuid);
          } else if (productUuid) {
            this.getVariationByUuid(productUuid)
              .pipe(takeUntil(this.destroy$))
              .subscribe({
                next: meta => {
                  this.savedRequiredFields = meta.requiredFields || [];
                  this.applyVariationDisplayMeta(meta);
                  this.showDomainField = this.checkRequiredFields(
                    this.savedRequiredFields
                  );
                  this.checkDomainType();
                  this.enforceDomainTypeRules();
                  this.maybeLoadMicrosoftTerms();
                  this.updateDomainValidation();
                  this.loading = false;
                  this.cdr.detectChanges();
                },
                error: () => {
                  this.loading = false;
                  this.cdr.detectChanges();
                },
              });
          } else {
            this.loading = false;
            this.cdr.detectChanges();
          }
        },
        error: error => {
          console.error('Erro ao buscar dados da empresa:', error);
          this.toastService.showError(
            this.i18nService.instant(
              'pages.registerToken.messages.invalidToken'
            ),
            5000,
            'top-center'
          );
          if (this.isLoggedInFlow) {
            this.router.navigate(['/my-account/pending-orders']);
          } else {
            this.router.navigate(['/login']);
          }
          this.loading = false;
        },
      });
  }

  getDomainSuffix(): string {
    return this.calculateDomainSuffix();
  }

  private get genericDomainFieldName():
    | 'com_br_domain'
    | 'email_domain'
    | 'com_domain'
    | 'net_domain'
    | null {
    if (!this.savedRequiredFields || this.savedRequiredFields.length === 0) {
      return null;
    }

    if (this.savedRequiredFields.some(field => field.name === 'email_domain')) {
      return 'email_domain';
    }
    if (this.savedRequiredFields.some(field => field.name === 'com_br_domain')) {
      return 'com_br_domain';
    }
    if (this.savedRequiredFields.some(field => field.name === 'com_domain')) {
      return 'com_domain';
    }
    if (this.savedRequiredFields.some(field => field.name === 'net_domain')) {
      return 'net_domain';
    }

    return null;
  }

  private getGenericDomainSuffix(
    fieldName: 'com_br_domain' | 'email_domain' | 'com_domain' | 'net_domain'
  ): string {
    switch (fieldName) {
      case 'email_domain':
      case 'com_br_domain':
        return '.com.br';
      case 'com_domain':
        return '.com';
      case 'net_domain':
        return '.net';
    }
  }

  private calculateDomainSuffix(): string {
    if (this.savedRequiredFields && this.savedRequiredFields.length > 0) {
      if (
        this.savedRequiredFields.some(
          field => field.name === 'microsoft_domain'
        )
      ) {
        return '.onmicrosoft.com';
      } else if (
        this.savedRequiredFields.some(
          field => field.name === 'google_ws_domain'
        )
      ) {
        return '';
      }

      const genericField = this.genericDomainFieldName;
      if (genericField) {
        return this.getGenericDomainSuffix(genericField);
      }
    }
    return '.com';
  }

  public checkDomainAvailability(): void {
    if (this.hasDomainFormatError) {
      return;
    }

    this.loading = true;
    this.domainValidationResult = null;
    this.ownershipManualVerificationError = false;
    this.selfConfirmedOwnership = false;
    this.googleDomainNotInUseAcknowledged = false;
    this.confirmedDomainOwnership = false;

    const completeDomain = `${this.tokenForm.get('domain')?.value}${this.getDomainSuffix()}`;

    this.domainAvailability(completeDomain);
  }

  public onDomainChange(_domain: string): void {
    this.domainValidationResult = null;
    this.ownershipManualVerificationError = false;
    this.selfConfirmedOwnership = false;
    this.googleDomainNotInUseAcknowledged = false;
    this.confirmedDomainOwnership = false;
  }

  public onDomainTypeChange(): void {
    this.domainValidationResult = null;
    this.ownershipManualVerificationError = false;
    this.selfConfirmedOwnership = false;
    this.googleDomainNotInUseAcknowledged = false;
    this.confirmedDomainOwnership = false;
    const domainControl = this.tokenForm.get('domain');
    if (domainControl?.value) {
      domainControl.setValue('');
    }

    this.enforceDomainTypeRules();
  }

  public onConfirmDomainOwnership(): void {
    this.ownershipManualVerificationError = false;
    this.selfConfirmedOwnership = true;
    this.googleDomainNotInUseAcknowledged = false;
    this.confirmedDomainOwnership = false;
    this.domainValidationResult = true;
    this.loading = false;
  }

  public onChangeDomainOwnership(): void {
    this.ownershipManualVerificationError = false;
    this.selfConfirmedOwnership = false;
    this.googleDomainNotInUseAcknowledged = false;
    this.confirmedDomainOwnership = false;
    this.domainValidationResult = null;
    this.tokenForm.patchValue({ domain: '' }, { emitEvent: false });
    this.cdr.detectChanges();
  }

  public get shouldShowGoogleDomainNotInUseWarning(): boolean {
    const domainType = this.tokenForm?.get('domainType')?.value;
    return (
      this.isGoogle &&
      domainType === 'existing' &&
      this.domainValidationResult === true &&
      this.selfConfirmedOwnership
    );
  }

  public get shouldShowEmailDomainOwnershipWarning(): boolean {
    const domainType = this.tokenForm?.get('domainType')?.value;
    return (
      this.isEmailEmpresarialProduct &&
      domainType === 'existing' &&
      this.domainValidationResult === true &&
      this.selfConfirmedOwnership
    );
  }

  public onGoogleDomainNotInUseAcknowledgedChange(event: Event): void {
    this.googleDomainNotInUseAcknowledged = (
      event.target as HTMLInputElement
    ).checked;
  }

  public onConfirmedDomainOwnershipChange(event: Event): void {
    this.confirmedDomainOwnership = (event.target as HTMLInputElement).checked;
  }

  public getVerifyDomainFeedback(): DomainFeedback | null {
    if (!this.showDomainField) return null;

    const suffix = this.getDomainSuffix();
    const domainValue = (this.tokenForm.get('domain')?.value as string) || '';
    const completeDomain = `${domainValue}${suffix}`.trim();
    const domainType = this.tokenForm.get('domainType')?.value || 'new';

    if (this.ownershipManualVerificationError) {
      const fullMessage = this.i18nService.instant(
        'pages.productConfig.domain.ownershipManualVerification',
        { domain: completeDomain || domainValue }
      );

      const [rawTitle, ...rawDescriptionParts] = fullMessage.split('\n');
      const title = (rawTitle || '').trim().replace(/\.$/, '');
      const description = rawDescriptionParts
        .join('\n')
        .trim()
        .replace(/\.$/, '');

      return {
        type: 'warning',
        title,
        description,
        isDomainFree: false,
      };
    }

    // Google existing: valid=true no endpoint significa disponível → após inversão, falha = não registrado.
    if (
      this.isGoogle &&
      domainType === 'existing' &&
      this.domainValidationResult === false
    ) {
      return {
        type: 'error',
        title: this.i18nService.instant(
          'pages.productConfig.domain.existingDomainNotRegisteredTitle'
        ),
        description: this.i18nService.instant(
          'pages.productConfig.domain.existingDomainNotRegisteredDescription',
          { domain: completeDomain || domainValue }
        ),
        isDomainFree: false,
      };
    }

    if (
      domainType === 'existing' &&
      this.domainValidationResult === true &&
      !this.isMicrosoft
    ) {
      return {
        type: 'success',
        title: this.i18nService.instant(
          'pages.productConfig.domain.ownershipConfirmedTitle'
        ),
        description: this.i18nService.instant(
          'pages.productConfig.domain.ownershipConfirmedDescription'
        ),
        isDomainFree: false,
      };
    }

    return null;
  }

  private domainAvailability(completeDomain: string): void {
    if (this.getDomainSuffix() === '.onmicrosoft.com') {
      this.validateMicrosoftDomain(completeDomain);
    } else if (this.getDomainSuffix() === '') {
      this.validateGoogleDomain(completeDomain);
    } else {
      this.validateGenericDomain(completeDomain);
    }
  }

  private validateGoogleDomain(completeDomain: string): void {
    if (!this.isValidGoogleDomainFormat(completeDomain)) {
      this.handleValidationResult(false, null, completeDomain);
      return;
    }

    const domainType = this.tokenForm.get('domainType')?.value || 'new';

    this.validationService
      .checkGoogleWorkspaceDomainAvailability(completeDomain)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          if (domainType === 'new') {
            this.handleValidationResult(response.valid, null, completeDomain);
            return;
          }

          // existing: valid=true no endpoint significa disponível (não existe).
          if (response.valid) {
            this.ownershipManualVerificationError = false;
            this.handleValidationResult(false, null, completeDomain);
            return;
          }

          this.validateExistingDomainOwnership(completeDomain);
        },
        error: _error => this.handleValidationError(),
      });
  }

  private isValidGoogleDomainFormat(domain: string): boolean {
    return isValidDomain(domain);
  }

  private validateExistingDomainOwnership(completeDomain: string): void {
    const email = (this.tokenForm.get('email')?.value as string) || '';
    const cnpj = (this.tokenForm.get('cnpj')?.value as string) || '';

    if (!email) {
      this.ownershipManualVerificationError = true;
      this.handleValidationResult(false, null, completeDomain);
      return;
    }

    this.validationService
      .validateDomainOwnership(completeDomain, email)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ownershipByEmail => {
          if (ownershipByEmail.isValid) {
            this.ownershipManualVerificationError = false;
            this.selfConfirmedOwnership = false;
            this.handleValidationResult(true, null, completeDomain);
            return;
          }

          if (!cnpj) {
            this.ownershipManualVerificationError = true;
            this.handleValidationResult(false, null, completeDomain);
            return;
          }

          this.validationService
            .validateDomainOwnership(completeDomain, cnpj)
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: ownershipByCnpj => {
                const isOwner = Boolean(ownershipByCnpj.isValid);
                this.ownershipManualVerificationError = !isOwner;
                if (isOwner) {
                  this.selfConfirmedOwnership = false;
                }
                this.handleValidationResult(isOwner, null, completeDomain);
              },
              error: () => this.handleValidationError(),
            });
        },
        error: () => this.handleValidationError(),
      });
  }

  private validateMicrosoftDomain(completeDomain: string): void {
    const domainType = this.tokenForm.get('domainType')?.value || 'new';
    const params = this.createMicrosoftParams(completeDomain, domainType);

    this.validationService
      .checkMicrosoftTenantIdAvailability(params)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: response => {
          const isValid = response.data.isAvailable;
          const needAuthorization = !isValid && domainType === 'existing';
          if (needAuthorization) {
            this.showAuthorizationModal = true;
            this.domainNeedingAuthorization = completeDomain;
            this.pendingDomainForRevalidation = completeDomain;
            this.loading = false;
            return;
          }

          this.handleValidationResult(isValid, null, completeDomain);
        },
        error: error => {
          const domainType = this.tokenForm.get('domainType')?.value || 'new';
          if (domainType === 'existing') {
            this.handleValidationResult(false, error, completeDomain);
          } else {
            this.handleValidationError();
          }
        },
      });
  }

  private validateGenericDomain(completeDomain: string): void {
    const domainType = this.tokenForm.get('domainType')?.value || 'new';

    this.validationService
      .checkRegistroBrDomainAvailability(completeDomain)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: RegistroBrDomainAvailabilityResponse) => {
          const isAvailable = response.isAvailable ?? false;

          // domainType === 'new': domínio precisa estar disponível
          if (domainType === 'new') {
            this.ownershipManualVerificationError = false;
            this.handleValidationResult(
              Boolean(isAvailable),
              null,
              completeDomain
            );
            return;
          }

          // domainType === 'existing': domínio precisa estar registrado
          if (isAvailable) {
            this.ownershipManualVerificationError = false;
            this.handleValidationResult(false, null, completeDomain);
            return;
          }

          // Domínio em uso + existing: validar ownership
          this.validateExistingDomainOwnership(completeDomain);
        },
        error: () => this.handleValidationError(),
      });
  }

  private createMicrosoftParams(
    completeDomain: string,
    domainType: string = 'new'
  ): MicrosoftTenantIdValidationParams {
    return {
      productUuid: this.tokenForm.get('productUuid')?.value || '',
      isVariation: true,
      userUuid: this.tokenForm.get('userUuid')?.value || '',
      tenantId: completeDomain,
      tenantPreference: domainType,
    };
  }

  private handleValidationResult(
    isValid: boolean,
    _error: unknown,
    domain: string
  ): void {
    if (_error && _error instanceof HttpErrorResponse) {
      const errorMessage = _error.error?.message || _error.error?.msg || '';

      // Verifica se é a mensagem específica do Microsoft que requer autorização
      if (this.isMicrosoftPartnerRelationshipError(errorMessage)) {
        this.handleMicrosoftPartnerRelationshipError(domain);
        return;
      }
    }

    this.domainValidationResult = isValid;
    this.loading = false;
  }

  private handleValidationError(): void {
    this.domainValidationResult = false;
    this.loading = false;
  }

  private applyVariationDisplayMeta(meta: {
    productName: string;
    planName: string;
    icon: Icon;
  }): void {
    this.productName = meta.productName;
    this.planName = meta.planName;
    this.productIcon = meta.icon;
  }

  /** Carrega apenas o resumo visual; falha não bloqueia o formulário. */
  private loadVariationSummary(productUuid: string): void {
    if (!productUuid) {
      return;
    }

    this.getVariationByUuid(productUuid)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: meta => {
          this.applyVariationDisplayMeta(meta);
          this.cdr.detectChanges();
        },
        error: error => {
          console.error('Erro ao buscar variação do produto:', error);
          this.toastService.showError(
            this.i18nService.instant(
              'pages.registerToken.messages.productVariationError'
            ),
            5000,
            'top-center'
          );
          this.cdr.detectChanges();
        },
      });
  }

  private mapVariationApiResponse(variation: unknown): {
    requiredFields: RequiredField[];
    productName: string;
    planName: string;
    icon: Icon;
  } {
    const raw = (variation ?? null) as
      | ({ data?: unknown } & Record<string, unknown>)
      | null;
    const d = (raw?.data ?? raw) as Record<string, unknown> | null;

    const requiredFields = (
      Array.isArray(d?.['requiredFields']) ? d?.['requiredFields'] : []
    ) as RequiredField[];
    const planName =
      typeof d?.['name'] === 'string' ? (d?.['name'] as string) : '';
    const product = (d?.['product'] ?? null) as Record<string, unknown> | null;
    const productName =
      typeof product?.['name'] === 'string'
        ? (product['name'] as string)
        : typeof d?.['productName'] === 'string'
          ? (d['productName'] as string)
          : '';
    const categories = (product?.['categories'] ??
      d?.['categories'] ??
      []) as ProductV2Category[];
    const rawProductType = product?.['productType'] ?? d?.['productType'];
    const productType =
      typeof rawProductType === 'string' ? rawProductType : undefined;
    const icon = this.categoryMapper.mapProductToIcon(categories, productType);
    return { requiredFields, productName, planName, icon };
  }

  private getVariationByUuid(uuid: string): Observable<{
    requiredFields: RequiredField[];
    productName: string;
    planName: string;
    icon: Icon;
  }> {
    return this.productService
      .getProductVariationByUuid(uuid)
      .pipe(
        map((variation: unknown) => this.mapVariationApiResponse(variation))
      );
  }

  private buildSelfConfirmedOwnershipField():
    | { selfConfirmedOwnership: boolean }
    | Record<string, never> {
    const domainType = this.tokenForm.get('domainType')?.value;
    if (
      !this.showDomainField ||
      domainType !== 'existing' ||
      this.isMicrosoft
    ) {
      return {};
    }

    return { selfConfirmedOwnership: this.selfConfirmedOwnership };
  }

  private buildConfirmedNoOtherGoogleAccountField():
    | { confirmedNoOtherGoogleAccount: boolean }
    | Record<string, never> {
    const domainType = this.tokenForm.get('domainType')?.value;
    if (
      !this.showDomainField ||
      domainType !== 'existing' ||
      !this.isGoogle ||
      !this.selfConfirmedOwnership
    ) {
      return {};
    }

    return {
      confirmedNoOtherGoogleAccount: this.googleDomainNotInUseAcknowledged,
    };
  }

  private buildConfirmedDomainOwnershipField():
    | { confirmedDomainOwnership: boolean }
    | Record<string, never> {
    if (!this.isEmailEmpresarialProduct) {
      return {};
    }

    const domainType = this.tokenForm.get('domainType')?.value;
    if (domainType === 'none') {
      return { confirmedDomainOwnership: false };
    }

    if (
      !this.showDomainField ||
      domainType !== 'existing' ||
      !this.selfConfirmedOwnership
    ) {
      return {};
    }

    return {
      confirmedDomainOwnership: this.confirmedDomainOwnership,
    };
  }

  private buildRequiredFields(): RequiredField[] {
    const requiredFields: RequiredField[] = [];

    if (!this.showDomainField || !this.domainValidationResult) {
      return requiredFields;
    }

    const domainValue = this.tokenForm.get('domain')?.value;
    if (!domainValue) {
      return requiredFields;
    }

    const completeDomain = `${domainValue}${this.getDomainSuffix()}`;

    const hasMicrosoftDomain = this.savedRequiredFields.some(
      field => field.name === 'microsoft_domain'
    );
    const hasGoogleDomain = this.savedRequiredFields.some(
      field => field.name === 'google_ws_domain'
    );
    const genericDomainField = this.genericDomainFieldName;
    if (genericDomainField) {
      requiredFields.push({
        name: genericDomainField,
        label: genericDomainField,
        type: 'TEXT',
        description: '',
        value: completeDomain,
      });

      const domainType = this.tokenForm.get('domainType')?.value;
      requiredFields.push({
        name: 'domain_preference',
        label: 'domain_preference',
        type: 'TEXT',
        description: '',
        value: domainType,
      });
    }

    if (hasGoogleDomain) {
      requiredFields.push({
        name: 'google_ws_domain',
        label: 'Domínio Google Workspace',
        type: 'TEXT',
        description: '',
        value: completeDomain,
      });

      const adminField = this.savedRequiredFields.find(
        f => f.name === 'google_ws_admin_email'
      );
      if (adminField) {
        requiredFields.push({
          name: 'google_ws_admin_email',
          label: 'Admin',
          type: 'TEXT',
          description: '',
          value: 'admin',
        });
      }
    }

    if (hasMicrosoftDomain) {
      requiredFields.push({
        name: 'microsoft_domain',
        label: 'Domínio da Microsoft',
        type: 'TEXT',
        description: '',
        value: completeDomain,
      });

      const domainType = this.tokenForm.get('domainType')?.value || 'new';
      requiredFields.push({
        name: 'tenant_preference',
        label: 'Tenant Preference',
        type: 'TEXT',
        description: '',
        value: domainType,
      });
      const emailField = this.savedRequiredFields.find(
        f => f.name === 'email_address_agreement'
      );
      if (emailField) {
        requiredFields.push({
          name: 'email_address_agreement',
          label: 'Email de Confirmação',
          type: 'TEXT',
          description: '',
          value: this.tokenForm.get('email')?.value || '',
        });
      }
    }

    return requiredFields;
  }

  private checkRequiredFields(requiredFields: RequiredField[]): boolean {
    const hasDomainField = requiredFields.some(
      (field: RequiredField) =>
        field.name === 'google_ws_domain' ||
        field.name === 'microsoft_domain' ||
        field.name === 'com_br_domain' ||
        field.name === 'email_domain' ||
        field.name === 'br_com_domain' ||
        field.name === 'com_domain' ||
        field.name === 'net_domain'
    );
    return hasDomainField;
  }

  private checkDomainType(): void {
    this.isMicrosoft = this.savedRequiredFields.some(
      field => field.name === 'microsoft_domain'
    );
    this.isGoogle = this.savedRequiredFields.some(
      field => field.name === 'google_ws_domain'
    );
    const genericField = this.genericDomainFieldName;
    this.isComBr = genericField === 'com_br_domain' || genericField === 'email_domain';
    this.isCom = genericField === 'com_domain';
    this.isNet = genericField === 'net_domain';
    const shouldShowRadioButtons =
      this.isMicrosoft ||
      this.isGoogle ||
      this.isComBr ||
      this.isCom ||
      this.isNet;
    this.showDomainRadioButtons =
      shouldShowRadioButtons &&
      (this.isExistingDomainOptionAllowed || this.isNewDomainOptionAllowed);

    this.enforceDomainTypeRules();
  }

  private hasRequiredFieldsInInitial(requiredFields: RequiredField[]): boolean {
    return requiredFields.some(
      (field: RequiredField) =>
        field.name === 'google_ws_domain' || field.name === 'microsoft_domain'
    );
  }

  private initializeForm(): void {
    this.tokenForm = this.fb.group({
      cnpj: [
        { value: '', disabled: true },
        [Validators.required, CustomValidators.cnpj],
      ],
      razaoSocial: [
        { value: '', disabled: true },
        [Validators.required, Validators.maxLength(255)],
      ],
      email: [
        '',
        [
          Validators.required,
          Validators.email,
          CustomValidators.emailWithDomainValidator(),
        ],
      ],
      domain: [''],
      domainType: ['new'],
      productUuid: [''],
      userUuid: [''],
    });
  }

  private setupFormListeners(): void {
    this.setupDomainListener();
  }

  private setupDomainListener(): void {
    this.tokenForm
      .get('domain')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(_domain => {
        this.domainValidationResult = null;
      });

    this.tokenForm
      .get('domainType')
      ?.valueChanges.pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.domainValidationResult = null;
        const domainControl = this.tokenForm.get('domain');
        if (domainControl?.value) {
          domainControl.setValue('');
        }
      });
  }

  private updateDomainValidation(): void {
    const domainControl = this.tokenForm.get('domain');
    if (this.needsDomainVerification) {
      domainControl?.setValidators([
        Validators.required,
        CustomValidators.noWhitespaceOnly,
      ]);
      domainControl?.updateValueAndValidity();
    } else {
      domainControl?.setValue('');
      domainControl?.clearValidators();
      domainControl?.updateValueAndValidity();
    }
  }

  goToLogin(): void {
    this.router.navigate(['/login']);
  }

  onMicrosoftTermsAcceptedChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.microsoftTermsAccepted = target.checked;
  }

  private maybeLoadMicrosoftTerms(): void {
    if (!this.isMicrosoft) {
      return;
    }
    this.loadMicrosoftTerms();
  }

  private loadMicrosoftTerms(): void {
    this.externalTermsService
      .getTermsByType('PURCHASE', 'Microsoft')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: term => {
          if (term.fileUrl) {
            this.microsoftTermsUrl = term.fileUrl;
          }
          if (term.id) {
            this.acceptanceTermId = term.id;
          }
          this.cdr.detectChanges();
        },
        error: error => {
          console.error('Erro ao carregar termos Microsoft:', error);
        },
      });
  }

  submitTokenRegistration(): void {
    if (this.tokenForm.invalid) {
      this.showFormErrors(this.tokenForm);
      return;
    }

    if (!!this.microsoftTermsUrl && !this.microsoftTermsAccepted) {
      return;
    }

    if (this.needsDomainVerification && this.domainValidationResult !== true) {
      this.toastService.showError(
        this.i18nService.instant('pages.registerToken.messages.checkDomain'),
        5000,
        'top-center'
      );
      return;
    }

    if (this.shouldShowGoogleDomainNotInUseWarning && !this.googleDomainNotInUseAcknowledged) {
      return;
    }

    if (this.shouldShowEmailDomainOwnershipWarning && !this.confirmedDomainOwnership) {
      return;
    }

    this.loading = true;
    const formData = this.tokenForm.getRawValue();

    const tokenData: TokenUpdateRequest & {
      ipAddress?: string;
      acceptanceTermId?: string;
    } = {
      email: formData.email,
      ipAddress: this.deviceInfo?.ip || '',
      acceptanceTermId: this.acceptanceTermId,
      product: {
        requiredFields: this.buildRequiredFields(),
        ...this.buildSelfConfirmedOwnershipField(),
        ...this.buildConfirmedNoOtherGoogleAccountField(),
        ...this.buildConfirmedDomainOwnershipField(),
      },
      ...(this.isLoggedInFlow ? { originActivation: 'HUB' as const } : {}),
    };

    this.route.queryParams.pipe(takeUntil(this.destroy$)).subscribe(params => {
      const token = params['token'];

      this.integrationsService
        .completeOrder(token, tokenData)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: _response => {
            this.loading = false;

            if (this.isLoggedInFlow) {
              this.channelEventsService
                .sendOrderComplementedEvent({
                  token,
                  productUuid: formData.productUuid || this.productUuid || undefined,
                  productName: this.productName || undefined,
                  variationName: this.planName || undefined,
                  email: formData.email || undefined,
                  cnpj: formData.cnpj || undefined,
                  companyName: formData.razaoSocial || undefined,
                  originActivation: 'HUB',
                  status: 'COMPLETED',
                })
                .subscribe();

              this.orderSuccessGuard.grantAccess();
              this.router.navigate(['/order-success'], {
                state: { flow: 'cpcActivation', email: formData.email },
              });
              return;
            }

            this.toastService.showSuccess(
              'Pedido concluído com sucesso!',
              5000,
              'top-center'
            );

            try {
              if (typeof sessionStorage !== 'undefined') {
                sessionStorage.setItem(
                  'umkt_success_register_context',
                  JSON.stringify({
                    email: formData.email,
                    isTokenFlow: true,
                    createdAt: Date.now(),
                  })
                );
              }
            } catch (error) {
              console.error(
                'Erro ao salvar contexto de sucesso no sessionStorage:',
                error
              );
            }

            this.router.navigate(['/success-register'], {
              state: { email: formData.email, isTokenFlow: true },
            });
          },
          error: error => {
            this.loading = false;
            this.handleTokenRegistrationError(error);
          },
        });
    });
  }

  private handleTokenRegistrationError(error: unknown): void {
    console.error('Erro no cadastro com token:', error);

    let errorMessage = this.i18nService.instant(
      'pages.registerToken.messages.error'
    );

    if (error && typeof error === 'object' && 'error' in error) {
      const errorObj = error as { error?: { msg?: string }; status?: number };
      if (errorObj.error?.msg) {
        errorMessage = errorObj.error.msg;
      } else if (errorObj.status === 400) {
        errorMessage = this.i18nService.instant(
          'pages.registerToken.messages.invalidData'
        );
      } else if (errorObj.status === 404) {
        errorMessage = this.i18nService.instant(
          'pages.registerToken.messages.invalidToken'
        );
      }
    }

    this.toastService.showError(errorMessage, 5000, 'top-center');
  }

  private showFormErrors(form: FormGroup): void {
    Object.keys(form.controls).forEach(key => {
      const control = form.get(key);
      if (control?.invalid) {
        control.markAsTouched();
      }
    });

    this.toastService.showError(
      this.i18nService.instant('pages.registerToken.messages.formError'),
      5000,
      'top-center'
    );
  }

  get cnpjControl() {
    return this.tokenForm.get('cnpj');
  }
  get razaoSocialControl() {
    return this.tokenForm.get('razaoSocial');
  }
  get emailControl() {
    return this.tokenForm.get('email');
  }
  get domainControl() {
    return this.tokenForm.get('domain');
  }
  get domainTypeControl() {
    return this.tokenForm.get('domainType');
  }

  get verifyButtonText(): string {
    if (this.loading) {
      return 'pages.registerToken.domain.checking';
    }

    if (
      this.isMicrosoft &&
      this.isExistingDomainOptionAllowed &&
      this.tokenForm.get('domainType')?.value === 'existing'
    ) {
      return 'pages.productConfig.domain.verifyExisting';
    }

    return 'pages.registerToken.domain.checkButton';
  }

  get hasDomainFormatError(): boolean {
    const domain = (this.tokenForm.get('domain')?.value as string) || '';
    if (!domain) {
      return false;
    }
    return !isValidDomainInput(domain, this.getDomainSuffix());
  }

  get showFreeDomainLabel(): boolean {
    return (
      this.isFreeDomainIncludedProduct &&
      this.tokenForm.get('domainType')?.value === 'new'
    );
  }

  public onCloseAuthorizationModal(): void {
    this.showAuthorizationModal = false;
    this.domainNeedingAuthorization = '';
    this.pendingDomainForRevalidation = '';
    this.loading = false;
  }

  public onAuthorizeDomain(): void {
    window.open(
      'https://admin.microsoft.com/Adminportal/Home?invType=ResellerRelationship&partnerId=b1778d46-80f9-4781-b3a0-851385f3edae&msppId=0#/partners/invitation',
      '_blank'
    );
    this.showAuthorizationModal = false;
    this.showAuthorizationConfirmationModal = true;
  }

  public onConfirmAuthorization(): void {
    this.showAuthorizationConfirmationModal = false;
    this.revalidateDomainAfterAuthorization();
  }

  private revalidateDomainAfterAuthorization(): void {
    if (!this.pendingDomainForRevalidation) return;

    this.loading = true;
    this.domainValidationResult = null;

    this.validateMicrosoftDomain(this.pendingDomainForRevalidation);
  }

  private isMicrosoftPartnerRelationshipError(errorMessage: string): boolean {
    if (!errorMessage) return false;
    const hasTenantIdNotAvailable = errorMessage.includes(
      'Tenant ID is not available'
    );
    const hasPartnerRelationship = errorMessage.includes(
      'establish a partner relationship'
    );
    const hasMicrosoftAdminLink = errorMessage.includes('admin.microsoft.com');
    return (
      hasTenantIdNotAvailable && hasPartnerRelationship && hasMicrosoftAdminLink
    );
  }

  private handleMicrosoftPartnerRelationshipError(
    completeDomain: string
  ): void {
    this.showAuthorizationModal = true;
    this.domainNeedingAuthorization = completeDomain;
    this.pendingDomainForRevalidation = completeDomain;
    this.loading = false;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
